import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FileText, Layers } from "lucide-react";
import {
  useMyUpgradeReviewsQuery,
  useDecideUpgradeReviewMutation,
} from "../api/committeeReviewApi";
import LoadError from "../components/LoadError";
// Shared with the entry reviews screen: the same evidence, shown the same way.
import { FileLink, certOf, subjectsText } from "../components/evidence";

function grade(app: any) {
  const from = app?.from_category?.name ?? app?.fromCategory?.name;
  const to = app?.to_category?.name ?? app?.toCategory?.name;
  return [from, to].filter(Boolean).join(" → ") || "Membership upgrade";
}

const dash = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));


function DecisionBadge({ decision }: { decision: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${map[decision] || map.pending}`}>
      {decision}
    </span>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">{dash(value)}</div>
    </div>
  );
}

/** A repeated block (qualifications, experience…) rendered as a small table. */
function Section({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: any[];
  columns: { label: string; get: (r: any) => any }[];
}) {
  if (!rows?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">{title}</h4>
      <div className="border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-gray-400 border-b">
              {columns.map((c) => (
                <th key={c.label} className="px-3 py-2">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r: any, i: number) => (
              <tr key={r.id ?? i}>
                {columns.map((c) => {
                  const value = c.get(r);

                  return (
                    <td key={c.label} className="px-3 py-2 text-gray-700">
                      {/* A column may return markup — a file link, say. Passing
                          that through dash() would String() the element and
                          print "[object Object]". */}
                      {React.isValidElement(value) ? value : dash(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The application itself. A recommendation cannot be made from a name and a
 * grade, so everything the applicant submitted is here.
 */
function ApplicationDetails({ app }: { app: any }) {
  const refName = (r: any) =>
    r.member
      ? [r.member.surname, r.member.firstname, r.member.othernames].filter(Boolean).join(" ")
      : "—";

  return (
    <div className="space-y-6 pt-4 border-t">
      <div>
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Applicant</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Name" value={[app.title, app.surname, app.other_names].filter(Boolean).join(" ")} />
          <Field label="Gender" value={app.gender} />
          <Field label="Date of birth" value={app.date_of_birth} />
          <Field label="Nationality" value={app.nationality} />
          <Field label="State" value={app.state?.name} />
          <Field label="LGA" value={app.lga?.name} />
          <Field label="Chapter" value={app.chapter?.name ?? app.chapter_other} />
          <Field label="Sector" value={app.sector?.name} />
          <Field label="Email" value={app.email ?? app.user?.email} />
          <Field label="Phone" value={app.phone} />
          <Field label="Residential address" value={app.residential_address} />
          <Field label="Postal address" value={app.postal_address} />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Application</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Upgrade" value={grade(app)} />
          <Field label="Route" value={app.route} />
          <Field label="Status" value={app.status} />
          <Field label="Submitted" value={app.acknowledged_at} />
          <Field label="Chapter endorsed" value={app.chapter_accepted_at} />
          <Field label="Chapter comment" value={app.chapter_accept_comment} />
        </div>
      </div>

      <Section
        title="Qualifications"
        rows={app.qualifications ?? []}
        columns={[
          { label: "Institution", get: (r) => r.institution },
          { label: "Qualification", get: (r) => r.qualification },
          { label: "Year", get: (r) => r.year },
          { label: "Certificate", get: (r) => <FileLink path={certOf(r)} /> },
        ]}
      />

      <Section
        title="O'Level results"
        rows={app.olevel_results ?? []}
        columns={[
          { label: "Exam", get: (r) => String(r.exam_type ?? "").toUpperCase() },
          { label: "Year", get: (r) => r.exam_year },
          { label: "Exam no.", get: (r) => r.exam_number },
          { label: "Subjects", get: (r) => subjectsText(r.subjects) },
          { label: "Result", get: (r) => <FileLink path={r.result_file} /> },
        ]}
      />

      <Section
        title="Prelim / IJMB results"
        rows={app.prelim_results ?? []}
        columns={[
          { label: "Exam", get: (r) => r.exam_type ?? r.other_exam_name },
          { label: "Institution", get: (r) => r.institution },
          { label: "Year", get: (r) => r.exam_year },
          { label: "Subjects", get: (r) => subjectsText(r.subjects) },
          { label: "Result", get: (r) => <FileLink path={r.result_file} /> },
        ]}
      />

      <Section
        title="Professional memberships"
        rows={app.professional_memberships ?? []}
        columns={[
          { label: "Body", get: (r) => r.body_name },
          { label: "Stage passed", get: (r) => r.stage_passed },
          { label: "Certificate", get: (r) => <FileLink path={certOf(r)} /> },
        ]}
      />

      <Section
        title="Work experience"
        rows={app.experiences ?? []}
        columns={[
          { label: "Organisation", get: (r) => r.organization },
          { label: "Position", get: (r) => r.position },
          { label: "From", get: (r) => r.start_date },
          { label: "To", get: (r) => r.end_date ?? "Present" },
          { label: "Responsibilities", get: (r) => r.responsibilities },
        ]}
      />

      <Section
        title="Seminars & workshops"
        rows={app.seminars ?? []}
        columns={[
          { label: "Title", get: (r) => r.title },
          { label: "Date", get: (r) => r.date },
          { label: "Location", get: (r) => r.location },
        ]}
      />

      <Section
        title="Papers presented / published"
        rows={app.papers ?? []}
        columns={[
          { label: "Title", get: (r) => r.title },
          { label: "Presented at", get: (r) => r.presented_at },
          { label: "Year", get: (r) => r.year },
        ]}
      />

      <Section
        title="Benevolent contributions"
        rows={app.benevolences ?? []}
        columns={[
          { label: "Type", get: (r) => r.type },
          { label: "Description", get: (r) => r.description },
          { label: "Year", get: (r) => r.year },
        ]}
      />

      <Section
        title="Referees"
        rows={app.referees ?? []}
        columns={[
          { label: "Referee", get: refName },
          { label: "Membership no.", get: (r) => r.member?.membership_no },
          { label: "Relationship", get: (r) => r.relationship },
          { label: "Response", get: (r) => r.decision },
          { label: "Remarks", get: (r) => r.remarks },
        ]}
      />

      {app.dissertation_topic ? (
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
            Dissertation
          </h4>
          <p className="text-sm text-gray-800">{app.dissertation_topic}</p>
        </div>
      ) : null}
    </div>
  );
}

function ReviewCard({ review, onDecided }: { review: any; onDecided?: (text: string) => void }) {
  const app = review.application || {};
  const [decision, setDecision] = useState<"approved" | "rejected" | "">(
    review.decision === "pending" ? "" : review.decision,
  );
  const [remark, setRemark] = useState<string>(review.remark ?? "");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [decide, { isLoading }] = useDecideUpgradeReviewMutation();

  const submit = async () => {
    setBanner(null);
    if (!decision) {
      setBanner({ ok: false, text: "Select approve or reject." });
      return;
    }
    try {
      await decide({ reviewId: review.id, decision, remark: remark.trim() || undefined }).unwrap();
      setBanner({ ok: true, text: "Your review has been recorded." });
      // On the Pending tab this card leaves the list the moment the decision
      // lands, taking its own banner with it — so the confirmation is raised to
      // the cycle, where it survives the re-filter.
      onDecided?.(
        `${app.user?.name ?? "Application"} — recommended for ${
          decision === "approved" ? "approval" : "rejection"
        }.`,
      );
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || e?.message || "Could not save your review." });
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b bg-indigo-50/40 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900">{app.user?.name ?? "Applicant"}</div>
          <div className="text-sm text-gray-500">{grade(app)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{app.user?.email}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">Your decision:</span>
          <DecisionBadge decision={review.decision} />
        </div>
      </div>

      <div className="p-5 space-y-4">
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          <FileText className="w-4 h-4" />
          {open ? "Hide application" : "View application"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {open ? <ApplicationDetails app={app} /> : null}

        {banner ? (
          <div
            className={`text-sm rounded-lg p-3 border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                : "bg-red-50 border-red-100 text-red-700"
            }`}
          >
            {banner.text}
          </div>
        ) : null}

        <div className="flex gap-2 pt-2 border-t">
          <button
            onClick={() => setDecision("approved")}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
              decision === "approved"
                ? "bg-emerald-600 text-white border-emerald-600"
                : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            }`}
          >
            Recommend approval
          </button>
          <button
            onClick={() => setDecision("rejected")}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
              decision === "rejected"
                ? "bg-red-600 text-white border-red-600"
                : "border-red-200 text-red-600 hover:bg-red-50"
            }`}
          >
            Recommend rejection
          </button>
        </div>

        <textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          rows={3}
          placeholder="Your remark / reason (optional)"
          className="w-full rounded-lg border px-3 py-2 text-sm"
        />

        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={isLoading || !decision}
            className="px-5 py-2.5 rounded-lg bg-gradient-purple text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {isLoading ? "Saving…" : review.decision === "pending" ? "Submit review" : "Update review"}
          </button>
        </div>
      </div>
    </div>
  );
}

const CYCLE_STATUS_STYLE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  ongoing: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

/** Assignments with no cycle still have to be reachable, so they get their own group. */
const NO_CYCLE = "__none__";

type Cycle = {
  key: string;
  title: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  reviews: any[];
  counts: { all: number; pending: number; approved: number; rejected: number };
};

/** My assignments grouped into the cycles that produced them. */
function groupByCycle(reviews: any[]): Cycle[] {
  const map = new Map<string, Cycle>();

  for (const r of reviews) {
    const key = r.diet?.id ? String(r.diet.id) : NO_CYCLE;

    if (!map.has(key)) {
      map.set(key, {
        key,
        title: r.diet?.title ?? "Not in a review cycle",
        status: r.diet?.status,
        start_date: r.diet?.start_date,
        end_date: r.diet?.end_date,
        reviews: [],
        counts: { all: 0, pending: 0, approved: 0, rejected: 0 },
      });
    }

    const cycle = map.get(key)!;
    cycle.reviews.push(r);
    cycle.counts.all += 1;
    const d = String(r.decision ?? "pending");
    if (d === "approved" || d === "rejected" || d === "pending") cycle.counts[d] += 1;
  }

  // Whatever still needs a recommendation comes first — that is the work.
  // Array.from, not a spread: the project's tsconfig target predates iterating
  // a Map without --downlevelIteration.
  return Array.from(map.values()).sort((a, b) => {
    if (a.key === NO_CYCLE) return 1;
    if (b.key === NO_CYCLE) return -1;
    if (b.counts.pending !== a.counts.pending) return b.counts.pending - a.counts.pending;
    return a.title.localeCompare(b.title);
  });
}

function Pill({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${tone}`}
    >
      <b className="tabular-nums">{value}</b> {label}
    </span>
  );
}

function CycleList({ cycles, onOpen }: { cycles: Cycle[]; onOpen: (key: string) => void }) {
  return (
    <div className="space-y-3">
      {cycles.map((c) => (
        <button
          key={c.key}
          onClick={() => onOpen(c.key)}
          className="w-full text-left bg-white border rounded-2xl shadow-sm p-5 hover:border-indigo-300 hover:shadow transition"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <Layers className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{c.title}</span>
                  {c.status ? (
                    <span
                      className={`inline-flex text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                        CYCLE_STATUS_STYLE[c.status] ?? CYCLE_STATUS_STYLE.pending
                      }`}
                    >
                      {c.status}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {c.key === NO_CYCLE
                    ? "Assigned outside a review cycle"
                    : `${c.start_date || "—"} → ${c.end_date || "—"}`}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Pill label="assigned" value={c.counts.all} tone="bg-gray-50 text-gray-600 border-gray-200" />
                  <Pill
                    label="pending"
                    value={c.counts.pending}
                    tone={
                      c.counts.pending > 0
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-gray-50 text-gray-400 border-gray-200"
                    }
                  />
                  <Pill
                    label="approved"
                    value={c.counts.approved}
                    tone="bg-emerald-50 text-emerald-700 border-emerald-200"
                  />
                  <Pill label="rejected" value={c.counts.rejected} tone="bg-red-50 text-red-700 border-red-200" />
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 mt-2" />
          </div>
        </button>
      ))}
    </div>
  );
}

type Tab = "all" | "pending" | "approved" | "rejected";

const TABS: { key: Tab; label: string; active: string }[] = [
  { key: "all", label: "All", active: "bg-gray-900 text-white border-gray-900" },
  { key: "pending", label: "Pending", active: "bg-amber-600 text-white border-amber-600" },
  { key: "approved", label: "Approved", active: "bg-emerald-600 text-white border-emerald-600" },
  { key: "rejected", label: "Rejected", active: "bg-red-600 text-white border-red-600" },
];

function CycleReviews({ cycle, onBack }: { cycle: Cycle; onBack: () => void }) {
  const [tab, setTab] = useState<Tab>(cycle.counts.pending > 0 ? "pending" : "all");
  const [recorded, setRecorded] = useState<string | null>(null);

  const shown =
    tab === "all" ? cycle.reviews : cycle.reviews.filter((r: any) => String(r.decision ?? "pending") === tab);

  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-2xl shadow-sm p-5">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> All review cycles
        </button>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-bold text-gray-900">{cycle.title}</h2>
          {cycle.status ? (
            <span
              className={`inline-flex text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                CYCLE_STATUS_STYLE[cycle.status] ?? CYCLE_STATUS_STYLE.pending
              }`}
            >
              {cycle.status}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-gray-500">
          {cycle.key === NO_CYCLE
            ? "Assigned outside a review cycle"
            : `${cycle.start_date || "—"} → ${cycle.end_date || "—"}`}{" "}
          · {cycle.counts.all} assigned to you
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setRecorded(null);
              }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                tab === t.key ? t.active : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {t.label}{" "}
              <span className="tabular-nums opacity-70">({cycle.counts[t.key]})</span>
            </button>
          ))}
        </div>
      </div>

      {recorded ? (
        <div className="px-4 py-3 rounded-2xl text-sm font-semibold border bg-emerald-50 border-emerald-100 text-emerald-800">
          {recorded}
        </div>
      ) : null}

      {shown.length === 0 ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
          Nothing {tab === "all" ? "in this cycle" : `${tab} in this cycle`}.
        </div>
      ) : (
        shown.map((r: any) => <ReviewCard key={r.id} review={r} onDecided={setRecorded} />)
      )}
    </div>
  );
}

export default function MyUpgradeReviewsPage() {
  const { data: reviews = [], isLoading, isError, error, refetch } = useMyUpgradeReviewsQuery();
  const [openCycle, setOpenCycle] = useState<string | null>(null);

  const cycles = useMemo(() => groupByCycle(reviews), [reviews]);
  // Resolved from the freshly grouped list, so a recorded decision re-renders
  // the counts and tabs underneath the member instead of going stale.
  const current = openCycle ? cycles.find((c) => c.key === openCycle) ?? null : null;

  const pending = reviews.filter((r: any) => r.decision === "pending").length;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold">My Upgrade Reviews</h1>
        <p className="text-sm text-white/80 mt-1">
          Upgrade applications assigned to you by the secretariat, grouped by review cycle. Open a cycle,
          read each application, and record your recommendation.
        </p>
        {reviews.length ? (
          <p className="text-sm text-white/90 font-semibold mt-2">
            {pending} of {reviews.length} still awaiting your recommendation · {cycles.length} cycle
            {cycles.length === 1 ? "" : "s"}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="text-gray-600">Loading…</div>
      ) : isError ? (
        <LoadError error={error} onRetry={refetch} />
      ) : !reviews.length ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
          No applications are assigned to you right now.
        </div>
      ) : current ? (
        <CycleReviews cycle={current} onBack={() => setOpenCycle(null)} />
      ) : (
        <CycleList cycles={cycles} onOpen={setOpenCycle} />
      )}
    </div>
  );
}
